import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import WebhookModal from "./WebhookModal";
import WebhookTestModal from "./WebhookTestModal";
import WebhookLogsModal from "./WebhookLogsModal";
import webhookService from "../../../lib/webhookService";
import { useAuth } from "../../../contexts/AuthContext";
import { useGettingStarted } from "../../../hooks/useGettingStarted";

const WebhookManager = () => {
  const { user } = useAuth();
  const { completeWebhookStep } = useGettingStarted();
  const [webhooks, setWebhooks] = useState([]);
  const [webhookList, setWebhookList] = useState([]);

  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [loading, setLoading] = useState(false);

  // Confirmation modal state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Load webhooks on component mount
  useEffect(() => {
    if (user?.id) {
      loadWebhooks();
    }
  }, [user?.id]);


    useEffect(() => {
      LoadWebhookList();
  }, [user?.id]);

  const loadWebhooks = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { data, error } = await webhookService.getWebhooks(user.id);
      if (error) {
        console.error("Failed to load webhooks:", error);
      } else {
        setWebhooks(data || []);
      }
    } catch (error) {
      console.error("Error loading webhooks:", error);
    } finally {
      setLoading(false);
    }
  };


  const LoadWebhookList = async () => {
    setLoading(true);
    try {
      const { data, error } = await webhookService.getWebhooklists();
      if (error) {
        console.error("Failed to load webhooks:", error);
      } else {
        setWebhookList(data || []);
      }
    } catch (error) {
      console.error("Error loading webhook lists:", error);
    } finally {
      setLoading(false);
    }

  }

  //  const availableEvents = LoadWebhookList()
  //   {
  //     id: "message.sent",
  //     label: "Message Sent",
  //     description: "Triggered when a message is successfully sent",
  //   },
  //   {
  //     id: "message.delivered",
  //     label: "Message Delivered",
  //     description: "Triggered when a message is delivered to recipient",
  //   },
  //   {
  //     id: "message.read",
  //     label: "Message Read",
  //     description: "Triggered when a message is read by recipient",
  //   },
  //   {
  //     id: "message.failed",
  //     label: "Message Failed",
  //     description: "Triggered when message delivery fails",
  //   },
  //   {
  //     id: "user.joined",
  //     label: "User Joined",
  //     description: "Triggered when a user joins a channel",
  //   },
  //   {
  //     id: "user.left",
  //     label: "User Left",
  //     description: "Triggered when a user leaves a channel",
  //   },
  //   {
  //     id: "user.activity",
  //     label: "User Activity",
  //     description: "Triggered on user activity events",
  //   },
  //   {
  //     id: "channel.created",
  //     label: "Channel Created",
  //     description: "Triggered when a new channel is created",
  //   },
  //   {
  //     id: "channel.deleted",
  //     label: "Channel Deleted",
  //     description: "Triggered when a channel is deleted",
  //   },
  //   {
  //     id: "integration.connected",
  //     label: "Integration Connected",
  //     description: "Triggered when integration is established",
  //   },
  //   {
  //     id: "integration.disconnected",
  //     label: "Integration Disconnected",
  //     description: "Triggered when integration fails",
  //   },
  // ];

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-accent/10 text-accent border-accent/20";
      case "paused":
        return "bg-warning/10 text-warning border-warning/20";
      case "failed":
        return "bg-destructive/10 text-destructive border-destructive/20";
      case "disabled":
        return "bg-muted text-muted-foreground border-border";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const getSuccessRate = (webhook) => {
    if (
      !webhook ||
      typeof webhook.deliveryAttempts !== "number" ||
      typeof webhook.successfulDeliveries !== "number" ||
      webhook.deliveryAttempts === 0
    ) {
      return 0;
    }
    return Math.round(
      (webhook.successfulDeliveries / webhook.deliveryAttempts) * 100,
    );
  };

  const handleCreateWebhook = () => {
    setSelectedWebhook(null);
    setIsWebhookModalOpen(true);
  };

  const handleEditWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setIsWebhookModalOpen(true);
  };

  const handleTestWebhook = (webhook) => {
    setSelectedWebhook(webhook);
    setIsTestModalOpen(true);
  };

  const handleViewLogs = (webhook) => {
    setSelectedWebhook(webhook);
    setIsLogsModalOpen(true);
  };

  const handleDeleteWebhook = (webhookId) => {
    setConfirmConfig({
      title: "Delete Webhook",
      message:
        "Are you sure you want to delete this webhook? This action cannot be undone.",
      onConfirm: async () => {
        setLoading(true);
        try {
          const { error } = await webhookService.deleteWebhook(
            webhookId,
            user.id,
          );
          if (error) {
            console.error("Failed to delete webhook:", error);
            // You could show a toast notification here
          } else {
            setWebhooks((prev) => prev.filter((w) => w.id !== webhookId));
          }
        } catch (error) {
          console.error("Failed to delete webhook:", error);
        } finally {
          setLoading(false);
        }
      },
    });
    setConfirmOpen(true);
  };

  const handleToggleWebhookStatus = async (webhookId) => {
    setLoading(true);
    try {
      const { data, error } = await webhookService.toggleWebhookStatus(
        webhookId,
        user.id,
      );
      if (error) {
        console.error("Failed to toggle webhook status:", error);
      } else if (data) {
        setWebhooks((prev) => prev.map((w) => (w.id === webhookId ? data : w)));
      }
    } catch (error) {
      console.error("Failed to toggle webhook status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhook = async (webhookData) => {
    setLoading(true);
    try {
      if (selectedWebhook) {
        // Update existing webhook
        const { data, error } = await webhookService.updateWebhook(
          selectedWebhook.id,
          user.id,
          webhookData,
        );

        if (error) {
          console.error("Failed to update webhook:", error);
        } else if (data) {
          setWebhooks((prev) =>
            prev.map((w) => (w.id === selectedWebhook.id ? data : w)),
          );
          setIsWebhookModalOpen(false);
          setSelectedWebhook(null);
        }
      } else {
        // Create new webhook
        const { data, error } = await webhookService.createWebhook(
          user.id,
          webhookData,
        );

        if (error) {
          console.error("Failed to create webhook:", error);
        } else if (data) {
          setWebhooks((prev) => [data, ...prev]);
          setIsWebhookModalOpen(false);
          setSelectedWebhook(null);

          // Auto-complete Getting Started step 4
          await completeWebhookStep();

          // Dispatch event for real-time updates
          window.dispatchEvent(
            new CustomEvent("gettingStarted:stepCompleted", {
              detail: { stepId: 4, userId: user.id },
            }),
          );
        }
      }
    } catch (error) {
      console.error("Failed to save webhook:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getWebhookStats = () => {
    return {
      total: webhooks.length,
      active: webhooks.filter((w) => w.status === "active").length,
      totalDeliveries: webhooks.reduce(
        (sum, w) => sum + (w.delivery_attempts || 0),
        0,
      ),
      successRate:
        webhooks.length > 0
          ? Math.round(
              webhooks.reduce((sum, w) => sum + getSuccessRate(w), 0) /
                webhooks.length,
            )
          : 0,
    };
  };

  const stats = getWebhookStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Webhook Management
          </h2>
          <p className="text-muted-foreground mt-1">
            Configure webhooks to receive real-time notifications about events
            in your account
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={handleCreateWebhook}
            disabled={loading}
          >
            Add Webhook
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Webhooks</p>
              <p className="text-2xl font-bold text-foreground">
                {stats.total}
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <Icon name="Webhook" size={20} className="text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-accent">{stats.active}</p>
            </div>
            <div className="p-3 bg-accent/10 rounded-lg">
              <Icon name="CheckCircle" size={20} className="text-accent" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Deliveries</p>
              <p className="text-2xl font-bold text-foreground">
                {stats.totalDeliveries.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <Icon name="Send" size={20} className="text-blue-500" />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold text-foreground">
                {stats.successRate}%
              </p>
            </div>
            <div className="p-3 bg-accent/10 rounded-lg">
              <Icon name="TrendingUp" size={20} className="text-accent" />
            </div>
          </div>
        </div>
      </div>

      {/* Webhooks List */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            Your Webhooks
          </h3>
        </div>

        <div className="divide-y divide-border">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-semibold text-foreground">
                      {webhook.name}
                    </h4>
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(webhook.status)}`}
                    >
                      {webhook.status}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mb-3">
                    <Icon
                      name="Link"
                      size={14}
                      className="text-muted-foreground"
                    />
                    <code className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                      {webhook.url}
                    </code>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    {webhook.events?.slice(0, 3).map((event) => (
                      <span
                        key={event}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded border border-primary/20"
                      >
                        {event}
                      </span>
                    ))}
                    {webhook.events?.length > 3 && (
                      <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                        +{webhook.events.length - 3} more
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        Success Rate:
                      </span>
                      <div className="flex items-center space-x-2 mt-1">
                        <div
                          className={`text-sm font-medium ${
                            getSuccessRate(webhook) >= 95
                              ? "text-accent"
                              : getSuccessRate(webhook) >= 80
                                ? "text-warning"
                                : "text-destructive"
                          }`}
                        >
                          {getSuccessRate(webhook)}%
                        </div>
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              getSuccessRate(webhook) >= 95
                                ? "bg-accent"
                                : getSuccessRate(webhook) >= 80
                                  ? "bg-warning"
                                  : "bg-destructive"
                            }`}
                            style={{ width: `${getSuccessRate(webhook)}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <span className="text-muted-foreground">
                        Total Requests:
                      </span>
                      <p className="text-foreground mt-1 font-medium">
                        {(webhook.delivery_attempts || 0).toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <span className="text-muted-foreground">Failed:</span>
                      <p className="text-destructive mt-1 font-medium">
                        {(webhook.failed_deliveries || 0).toLocaleString()}
                      </p>
                    </div>

                    <div>
                      <span className="text-muted-foreground">
                        Last Triggered:
                      </span>
                      <p className="text-foreground mt-1 text-xs">
                        {formatDate(webhook.last_triggered)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleViewLogs(webhook)}
                    title="View Logs"
                  >
                    <Icon name="FileText" size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleTestWebhook(webhook)}
                    title="Test Webhook"
                  >
                    <Icon name="Zap" size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditWebhook(webhook)}
                    title="Edit Webhook"
                  >
                    <Icon name="Edit" size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggleWebhookStatus(webhook.id)}
                    title={
                      webhook.status === "active"
                        ? "Pause Webhook"
                        : "Activate Webhook"
                    }
                    disabled={loading}
                  >
                    <Icon
                      name={
                        webhook.status === "active"
                          ? "PauseCircle"
                          : "PlayCircle"
                      }
                      size={16}
                    />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteWebhook(webhook.id)}
                    title="Delete Webhook"
                    className="text-destructive hover:text-destructive"
                    disabled={loading}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {webhooks.length === 0 && (
            <div className="p-12 text-center">
              <Icon
                name="Webhook"
                size={48}
                className="text-muted-foreground mx-auto mb-4"
              />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                No webhooks configured
              </h3>
              <p className="text-muted-foreground mb-4">
                Get started by creating your first webhook to receive real-time
                notifications
              </p>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreateWebhook}
              >
                Create Webhook
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <WebhookModal
        isOpen={isWebhookModalOpen}
        onClose={() => {
          setIsWebhookModalOpen(false);
          setSelectedWebhook(null);
        }}
        onSave={handleSaveWebhook}
        webhook={selectedWebhook}
        availableEvents={webhookList}
        loading={loading}
      />

      <WebhookTestModal
        isOpen={isTestModalOpen}
        onClose={() => {
          setIsTestModalOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
        availableEvents={webhookList}
      />

      <WebhookLogsModal
        isOpen={isLogsModalOpen}
        onClose={() => {
          setIsLogsModalOpen(false);
          setSelectedWebhook(null);
        }}
        webhook={selectedWebhook}
      />

      {/* Confirm Modal */}
      <Modal
        open={confirmOpen}
        title={confirmConfig.title || "Please Confirm"}
        onClose={() => setConfirmOpen(false)}
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground">{confirmConfig.message}</p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setConfirmOpen(false);
                confirmConfig.onConfirm?.();
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Simple modal component for confirmations
const Modal = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-2 w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

export default WebhookManager;
